import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { fetchLearningCatalog } from '../../lib/assignedLearning';

const emptyTrackForm = {
  title: '',
  slug: '',
  description: '',
  content_category: 'Estate Planning Basics',
  display_priority: 100,
  is_active: true
};

const slugify = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const itemTitle = (item) => item.title_override || item.topic?.title || item.resource?.title || 'Learning Item';

const catalogDescription = (type, item) => {
  if (type === 'topic') return item.short_summary || item.content_category;
  return item.short_description || item.resource_type;
};

const LearningTracksAdminPage = () => {
  const { profile } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState('');
  const [isCreatingTrack, setIsCreatingTrack] = useState(false);
  const [trackForm, setTrackForm] = useState(emptyTrackForm);
  const [trackItems, setTrackItems] = useState([]);
  const [catalog, setCatalog] = useState({ topics: [], resources: [] });
  const [itemType, setItemType] = useState('topic');
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemSaving, setItemSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const selectedTrack = useMemo(
    () => isCreatingTrack ? null : tracks.find((track) => track.id === selectedTrackId),
    [tracks, selectedTrackId, isCreatingTrack]
  );

  const catalogOptions = itemType === 'topic' ? catalog.topics : catalog.resources;
  const selectedCatalogItem = catalogOptions.find((item) => item.id === selectedCatalogId);

  const loadTracks = async ({ preferredTrackId = selectedTrackId } = {}) => {
    const { data, error: tracksError } = await supabase
      .from('education_learning_tracks')
      .select('*')
      .order('display_priority', { ascending: true })
      .order('title', { ascending: true });

    if (tracksError) throw tracksError;

    const rows = data || [];
    setTracks(rows);

    const nextSelectedId = preferredTrackId && rows.some((track) => track.id === preferredTrackId)
      ? preferredTrackId
      : rows[0]?.id || '';
    setSelectedTrackId(nextSelectedId);
    setIsCreatingTrack(false);
    return nextSelectedId;
  };

  const loadTrackItems = async (trackId) => {
    if (!trackId) {
      setTrackItems([]);
      return;
    }

    const { data, error: itemsError } = await supabase
      .from('education_learning_track_items')
      .select(`
        id,
        track_id,
        topic_id,
        resource_id,
        title_override,
        description_override,
        display_priority,
        is_active,
        topic:education_topics(id, title, slug, content_category, short_summary),
        resource:education_resources(id, title, slug, resource_type, short_description, estimated_minutes)
      `)
      .eq('track_id', trackId)
      .order('display_priority', { ascending: true });

    if (itemsError) throw itemsError;
    setTrackItems(data || []);
  };

  useEffect(() => {
    const loadPage = async () => {
      if (profile?.role !== 'platform_admin') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const [catalogData] = await Promise.all([
          fetchLearningCatalog(),
          loadTracks()
        ]);
        setCatalog(catalogData);
      } catch (err) {
        console.error('Learning tracks admin load error:', err);
        setError(err.message || 'Unable to load learning tracks.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [profile?.role]);

  useEffect(() => {
    if (!selectedTrack) {
      setTrackForm(emptyTrackForm);
      setTrackItems([]);
      return;
    }

    setTrackForm({
      title: selectedTrack.title || '',
      slug: selectedTrack.slug || '',
      description: selectedTrack.description || '',
      content_category: selectedTrack.content_category || 'Estate Planning Basics',
      display_priority: selectedTrack.display_priority || 100,
      is_active: selectedTrack.is_active !== false
    });

    loadTrackItems(selectedTrack.id).catch((err) => {
      console.error('Learning track item load error:', err);
      setError(err.message || 'Unable to load track items.');
    });
  }, [selectedTrack?.id]);

  const updateTrackForm = (key, value) => {
    setTrackForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'title' && !selectedTrackId ? { slug: slugify(value) } : {})
    }));
  };

  const startNewTrack = () => {
    setIsCreatingTrack(true);
    setTrackForm(emptyTrackForm);
    setTrackItems([]);
    setNotice(null);
    setError(null);
  };

  const saveTrack = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      if (!trackForm.title.trim()) throw new Error('Track title is required.');
      if (!trackForm.slug.trim()) throw new Error('Track slug is required.');

      const payload = {
        title: trackForm.title.trim(),
        slug: slugify(trackForm.slug),
        description: trackForm.description.trim() || null,
        content_category: trackForm.content_category.trim() || 'Estate Planning Basics',
        display_priority: Number(trackForm.display_priority) || 100,
        is_active: trackForm.is_active,
        created_by_user_id: profile.id,
        updated_at: new Date().toISOString()
      };

      let saved;
      if (!isCreatingTrack && selectedTrackId) {
        const { data, error: updateError } = await supabase
          .from('education_learning_tracks')
          .update(payload)
          .eq('id', selectedTrackId)
          .select('*')
          .single();

        if (updateError) throw updateError;
        saved = data;
      } else {
        const { data, error: insertError } = await supabase
          .from('education_learning_tracks')
          .insert(payload)
          .select('*')
          .single();

        if (insertError) throw insertError;
        saved = data;
      }

      await loadTracks({ preferredTrackId: saved.id });
      setIsCreatingTrack(false);
      setNotice('Learning track saved.');
    } catch (err) {
      console.error('Learning track save error:', err);
      setError(err.message || 'Unable to save learning track.');
    } finally {
      setSaving(false);
    }
  };

  const addTrackItem = async () => {
    if (!selectedTrackId || !selectedCatalogId || itemSaving) return;

    try {
      setItemSaving(true);
      setError(null);
      setNotice(null);

      const nextPriority = (trackItems.at(-1)?.display_priority || 0) + 10;
      const { error: insertError } = await supabase
        .from('education_learning_track_items')
        .insert({
          track_id: selectedTrackId,
          topic_id: itemType === 'topic' ? selectedCatalogId : null,
          resource_id: itemType === 'resource' ? selectedCatalogId : null,
          display_priority: nextPriority,
          is_active: true
        });

      if (insertError) throw insertError;

      setSelectedCatalogId('');
      await loadTrackItems(selectedTrackId);
      setNotice('Track item added.');
    } catch (err) {
      console.error('Add track item error:', err);
      setError(err.message || 'Unable to add track item.');
    } finally {
      setItemSaving(false);
    }
  };

  const removeTrackItem = async (item) => {
    try {
      setItemSaving(true);
      setError(null);
      const { error: deleteError } = await supabase
        .from('education_learning_track_items')
        .delete()
        .eq('id', item.id);

      if (deleteError) throw deleteError;
      await loadTrackItems(selectedTrackId);
    } catch (err) {
      console.error('Remove track item error:', err);
      setError(err.message || 'Unable to remove track item.');
    } finally {
      setItemSaving(false);
    }
  };

  const moveTrackItem = async (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= trackItems.length) return;

    const current = trackItems[index];
    const swap = trackItems[nextIndex];

    try {
      setItemSaving(true);
      setError(null);

      const { error: firstError } = await supabase
        .from('education_learning_track_items')
        .update({ display_priority: swap.display_priority })
        .eq('id', current.id);
      if (firstError) throw firstError;

      const { error: secondError } = await supabase
        .from('education_learning_track_items')
        .update({ display_priority: current.display_priority })
        .eq('id', swap.id);
      if (secondError) throw secondError;

      await loadTrackItems(selectedTrackId);
    } catch (err) {
      console.error('Move track item error:', err);
      setError(err.message || 'Unable to reorder track item.');
    } finally {
      setItemSaving(false);
    }
  };

  if (profile?.role !== 'platform_admin') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <SafeIcon icon={FiIcons.FiLock} className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-lg font-bold text-slate-900">Platform admin access required</h1>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="h-10 w-10 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Learning Tracks</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create reusable education sequences that advisors and attorneys can assign to clients.
          </p>
        </div>
        <button
          type="button"
          onClick={startNewTrack}
          className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
          New Track
        </button>
      </div>

      {error && (
        <div className="rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {notice && (
        <div className="rounded-md border-l-4 border-green-400 bg-green-50 p-4 text-sm text-green-700">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">Tracks</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {tracks.length > 0 ? tracks.map((track) => (
              <button
                key={track.id}
                type="button"
                onClick={() => {
                  setIsCreatingTrack(false);
                  setSelectedTrackId(track.id);
                }}
                className={`block w-full px-5 py-4 text-left transition-colors ${
                  selectedTrackId === track.id ? 'bg-blue-50/70' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{track.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{track.content_category}</p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    track.is_active
                      ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-100 text-slate-500'
                  }`}>
                    {track.is_active ? 'Active' : 'Hidden'}
                  </span>
                </div>
              </button>
            )) : (
              <div className="p-6 text-center text-sm text-slate-500">
                No learning tracks yet.
              </div>
            )}
          </div>
        </aside>

        <div className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">
                {selectedTrackId ? 'Edit Track' : 'Create Track'}
              </h2>
            </div>
            <form onSubmit={saveTrack} className="space-y-5 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">
                  Title
                  <input
                    type="text"
                    value={trackForm.title}
                    onChange={(event) => updateTrackForm('title', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    required
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Slug
                  <input
                    type="text"
                    value={trackForm.slug}
                    onChange={(event) => updateTrackForm('slug', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                    required
                  />
                </label>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Description
                <textarea
                  rows={3}
                  value={trackForm.description}
                  onChange={(event) => updateTrackForm('description', event.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                />
              </label>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_140px_140px]">
                <label className="text-sm font-medium text-slate-700">
                  Category
                  <input
                    type="text"
                    value={trackForm.content_category}
                    onChange={(event) => updateTrackForm('content_category', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  Priority
                  <input
                    type="number"
                    value={trackForm.display_priority}
                    onChange={(event) => updateTrackForm('display_priority', event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="flex items-end gap-2 pb-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={trackForm.is_active}
                    onChange={(event) => updateTrackForm('is_active', event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Active
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                >
                  <SafeIcon icon={saving ? FiIcons.FiLoader : FiIcons.FiSave} className={`mr-2 h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                  Save Track
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-sm font-bold uppercase tracking-tight text-slate-800">Track Sequence</h2>
              <p className="mt-1 text-xs text-slate-500">Add topics or resources, then order them as the client should review them.</p>
            </div>

            <div className="space-y-5 p-6">
              {!selectedTrackId ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Save a track before adding sequence items.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-[120px_1fr_auto] lg:items-end">
                    <div>
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Type</label>
                      <select
                        value={itemType}
                        onChange={(event) => {
                          setItemType(event.target.value);
                          setSelectedCatalogId('');
                        }}
                        className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                      >
                        <option value="topic">Topic</option>
                        <option value="resource">Resource</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Item</label>
                      <select
                        value={selectedCatalogId}
                        onChange={(event) => setSelectedCatalogId(event.target.value)}
                        className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                      >
                        <option value="">Choose {itemType}</option>
                        {catalogOptions.map((item) => (
                          <option key={item.id} value={item.id}>{item.title}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={addTrackItem}
                      disabled={!selectedCatalogId || itemSaving}
                      className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                    >
                      <SafeIcon icon={itemSaving ? FiIcons.FiLoader : FiIcons.FiPlus} className={`mr-2 h-4 w-4 ${itemSaving ? 'animate-spin' : ''}`} />
                      Add
                    </button>
                  </div>

                  {selectedCatalogItem && (
                    <p className="rounded-md border border-slate-100 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
                      {catalogDescription(itemType, selectedCatalogItem)}
                    </p>
                  )}

                  {trackItems.length > 0 ? (
                    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {trackItems.map((item, index) => (
                        <div key={item.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                          <div className="min-w-0">
                            <div className="mb-1 flex items-center gap-2">
                              <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                {item.topic_id ? 'Topic' : 'Resource'}
                              </span>
                              <span className="text-xs text-slate-400">Step {index + 1}</span>
                            </div>
                            <p className="font-semibold text-slate-900">{itemTitle(item)}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {item.topic?.short_summary || item.resource?.short_description || item.topic?.content_category || item.resource?.resource_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => moveTrackItem(index, -1)}
                              disabled={index === 0 || itemSaving}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                              title="Move up"
                            >
                              <SafeIcon icon={FiIcons.FiArrowUp} className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveTrackItem(index, 1)}
                              disabled={index === trackItems.length - 1 || itemSaving}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                              title="Move down"
                            >
                              <SafeIcon icon={FiIcons.FiArrowDown} className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeTrackItem(item)}
                              disabled={itemSaving}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40"
                              title="Remove"
                            >
                              <SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                      No items in this track yet.
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LearningTracksAdminPage;
